import React from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider, useAuth, useFeature, usePermission } from './context/AuthContext';
import useAdminAccess from './context/useAdminAccess';
import { API_BASE_URL } from './config';
import usePageTitle from './hooks/usePageTitle';
import AdminDashboard from './components/AdminDashboard';
import AppDetailPage from './components/admin/apps/AppDetailPage';
import RoleEditorPage from './components/admin/roles/RoleEditorPage';
import TopBar from './components/TopBar';
import AnnouncementBanner from './components/announcement/AnnouncementBanner';
import { chromeFor } from './lib/routeChrome';
import Sidebar from './components/Sidebar';
import HomeRoute from './components/home/HomeRoute';
import NotFoundPage from './components/notFound/NotFoundPage';
import DeniedState, { LoadingState } from './components/common/DeniedState';
import RequestsHubPage from './components/requests/RequestsHubPage';
import BuildAppPage from './components/buildApp/BuildAppPage';
import UserAppDetailPage from './components/buildApp/UserAppDetailPage';
import LoginPage from './components/LoginPage';
import DepartmentPage from './components/department/DepartmentPage';
import ResultsChainPage from './components/resultsChain/ResultsChainPage';
import BusinessOntologyPage from './components/businessOntology/BusinessOntologyPage';
import OrgChartPage from './components/orgChart/OrgChartPage';
import ProfilePage from './components/profile/ProfilePage';
import VendorsPage from './components/vendors/VendorsPage';
import VendorDetailPage from './components/vendors/VendorDetailPage';
import EmployeeDirectoryPage from './components/directory/EmployeeDirectoryPage';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles.css';

// Same key as LoginPage. Kept here as a string so we don't add a cross-import
// cycle just to share a constant.
const POST_LOGIN_REDIRECT_KEY = 'rtg.postLoginRedirect';

const FeatureRoute = ({ flag, children }) => {
  const enabled = useFeature(flag);
  if (!enabled) {
    return <DeniedState title="Feature unavailable" message="This feature is currently turned off. Check back later." />;
  }
  return children;
};

// Flag off renders the 404 page (spec 4.4), not a "feature unavailable" notice,
// so a disabled module's URLs look unrouted. Waits for the flag map: flags fail
// open, and mounting the page first would fire requests the server then 403s.
const VendorsRoute = ({ children }) => {
  const enabled = useFeature('vendor_management');
  const { featuresLoaded } = useAuth() || {};
  if (featuresLoaded === false) return <LoadingState />;
  return enabled ? children : <NotFoundPage />;
};

// Plain-English denied copy per role: never show a raw role key to a user.
const ROLE_DENIED_COPY = {
  dept_manager: 'Only department managers can open this page.',
};

export const ProtectedRoute = ({ children, adminOnly = false, requireRole = null, adminOrManager = false, permission = null, adminAccess = false }) => {
  const { user, loading, adminAreas } = useAuth();
  const hasPermission = usePermission(permission);

  if (loading) {
    return (
      <LoadingState />
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  if (adminOnly && !user.is_admin) {
    return (
      <DeniedState title="Access denied" message="You do not have permission to access this page." />
    );
  }

  if (adminAccess && !user.is_admin && !(adminAreas || []).length) {
    return (
      <DeniedState title="Access denied" message="You do not have permission to access this page." />
    );
  }

  if (adminOrManager && !user.is_admin && !(user.roles || []).includes('dept_manager')) {
    return (
      <DeniedState title="Access denied" message="You do not have permission to access this page." />
    );
  }

  if (permission && !hasPermission) {
    return (
      <DeniedState title="Access denied" message="You do not have permission to access this page." />
    );
  }

  if (requireRole && !(user.roles || []).includes(requireRole) && !user.is_admin) {
    return (
      <DeniedState
        title="Access denied"
        message={ROLE_DENIED_COPY[requireRole] || 'You do not have permission to access this page.'}
      />
    );
  }

  return children;
};

// /admin lands on the first tab this user may open.
function AdminIndexRedirect() {
  const { isFullAdmin, areas } = useAdminAccess();
  const first = isFullAdmin ? 'overview' : areas[0];
  return <Navigate to={first ? `/admin/${first}` : '/'} replace />;
}

function AppContent() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  usePageTitle();
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);

  // Admin routes get their own shell (AdminShell renders its own sidebar +
  // topbar). Suppress the user-facing Sidebar/TopBar there so the two chromes
  // don't stack.
  const isAdminRoute = location.pathname.startsWith('/admin');
  const showUserChrome = !!user && !isAdminRoute;

  // Wide pages (org chart, business ontology; see lib/routeChrome) auto-collapse
  // the main sidebar to give them room. sidebarCollapsed has no persistence to clobber
  // (unlike the admin shell's own rtg.adminSidebarCollapsed localStorage key),
  // so this is a route-scoped override rather than a stored-preference write:
  // remember whatever the sidebar's state was on entry, force it collapsed,
  // and restore the remembered value on leaving. The reader can still expand
  // it by hand while on the page: the effect only fires on path transitions,
  // keyed on `collapseSidebar`, never on `setSidebarCollapsed` itself, which
  // is a stable setter identity from useState and does not need to be listed.
  const { collapseSidebar, fullBleed } = chromeFor(location.pathname);
  const orgChartEntryCollapsedRef = React.useRef(null);
  React.useEffect(() => {
    if (!collapseSidebar) return undefined;
    orgChartEntryCollapsedRef.current = sidebarCollapsed;
    setSidebarCollapsed(true);
    return () => {
      setSidebarCollapsed(orgChartEntryCollapsedRef.current ?? false);
      orgChartEntryCollapsedRef.current = null;
    };
    // Intentionally omits sidebarCollapsed: it is only read once, at the
    // instant the route is entered (via the ref snapshot above), and it must
    // NOT be a dependency, or every manual toggle while on the route would
    // re-run this effect and immediately force the sidebar back closed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapseSidebar]);

  // After a successful login, restore the URL the user originally requested
  // (e.g. /admin#tab=tools). LoginPage stashes this in sessionStorage before
  // bouncing to /auth/google; we read it here once the user object resolves.
  React.useEffect(() => {
    if (!user) return;
    let target;
    try { target = window.sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY); } catch { return; }
    if (!target) return;
    try { window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY); } catch { /* ignore */ }
    // Guard against open-redirect: must be a relative in-app path.
    if (!target.startsWith('/') || target.startsWith('//')) return;
    // If we're already on the right path, no-op (avoid history pollution).
    const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (here === target) return;
    navigate(target, { replace: true });
  }, [user, navigate]);

  return (
    <div className={`app-container ${showUserChrome ? 'app-layout' : ''} ${isAdminRoute ? 'app-admin-route' : ''}`}>
      {showUserChrome && (
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((c) => !c)}
        />
      )}
      <div
        className={`app-main ${showUserChrome && sidebarCollapsed ? 'sidebar-collapsed' : ''} ${isAdminRoute ? 'app-main--admin' : ''} ${fullBleed ? 'app-main--full-bleed' : ''}`}
      >
        {showUserChrome && <TopBar />}
        {/* Under the topbar, above the routed page, so one strip serves every
            employee surface rather than each page re-mounting its own. Inside
            `showUserChrome`, so it does not appear in the admin shell, which
            owns its own chrome. */}
        {showUserChrome && !fullBleed && <AnnouncementBanner />}
        <main className={`main-content ${isAdminRoute ? 'main-content--admin' : ''} ${fullBleed ? 'main-content--full-bleed' : ''}`}>
          <Routes>
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <HomeRoute />
                </ProtectedRoute>
              }
            />
            <Route
              path="/requests"
              element={
                <ProtectedRoute>
                  <RequestsHubPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/people"
              element={
                <ProtectedRoute>
                  <EmployeeDirectoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/:id"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/build-app"
              element={
                <ProtectedRoute>
                  <FeatureRoute flag="build_your_app">
                    <BuildAppPage />
                  </FeatureRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/build-app/:id"
              element={
                <ProtectedRoute>
                  <FeatureRoute flag="build_your_app">
                    <UserAppDetailPage />
                  </FeatureRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminAccess={true}>
                  <AdminIndexRedirect />
                </ProtectedRoute>
              }
            />
            {/* App detail is a standalone admin page — declare it BEFORE the
                generic /admin/:tab so "apps/:id" wins over ":tab". */}
            <Route
              path="/admin/apps/:id"
              element={
                <ProtectedRoute adminAccess={true}>
                  <AppDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/roles/:roleId"
              element={
                <ProtectedRoute adminOnly={true}>
                  <FeatureRoute flag="rbac_management">
                    <RoleEditorPage />
                  </FeatureRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/:tab"
              element={
                <ProtectedRoute adminAccess={true}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dept/:id"
              element={
                <ProtectedRoute requireRole="dept_manager">
                  <DepartmentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dept"
              element={
                <ProtectedRoute requireRole="dept_manager">
                  <DeptIndexRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/results-chain"
              element={
                <ProtectedRoute permission="results_chain.view">
                  <ResultsChainPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business-ontology"
              element={
                <ProtectedRoute permission="business_ontology.view">
                  <BusinessOntologyPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/org-chart"
              element={
                <ProtectedRoute permission="org_chart.view">
                  <OrgChartPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors"
              element={
                <ProtectedRoute>
                  <VendorsRoute><VendorsPage /></VendorsRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors/:id"
              element={
                <ProtectedRoute>
                  <VendorsRoute><VendorDetailPage /></VendorsRoute>
                </ProtectedRoute>
              }
            />
            {/* Last: any address no route above matches. Signed-out visitors
                still go to sign-in via ProtectedRoute. */}
            <Route
              path="*"
              element={
                <ProtectedRoute>
                  <NotFoundPage />
                </ProtectedRoute>
              }
            />
          </Routes>
        </main>
      </div>
    </div>
  );
}

// /dept lands on the first department this user manages. A 403 (or an empty
// list) means "you manage none"; any other failure is an error with retry,
// never "no departments", which would be a lie during an outage.
export function DeptIndexRedirect() {
  const [state, setState] = React.useState({ status: 'loading', target: null });
  const [attempt, setAttempt] = React.useState(0);
  React.useEffect(() => {
    let active = true;
    setState({ status: 'loading', target: null });
    axios
      .get(`${API_BASE_URL}/api/dept/managed`, { withCredentials: true })
      .then((r) => {
        const first = (r.data.departments || [])[0];
        if (active) setState(first ? { status: 'redirect', target: `/dept/${first.id}` } : { status: 'none', target: null });
      })
      .catch((err) => {
        if (active) setState({ status: err?.response?.status === 403 ? 'none' : 'error', target: null });
      });
    return () => { active = false; };
  }, [attempt]);

  if (state.status === 'redirect') return <Navigate to={state.target} replace />;
  return (
    <div className="main-content-inner">
      {state.status === 'loading' && (
        <p className="page-subtitle" role="status" aria-busy="true">Loading your departments…</p>
      )}
      {state.status === 'none' && (
        <DeniedState className="" eyebrow="My Departments" title="No departments" message="You don't manage any departments yet." />
      )}
      {state.status === 'error' && (
        <header className="page-head">
          <h1 className="page-title">My Departments</h1>
          <div role="alert">
            <p className="page-subtitle">Couldn&apos;t load your departments. Check your connection and try again.</p>
            <button type="button" className="btn btn-secondary btn-small" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
          </div>
        </header>
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
        <ToastContainer
          position="top-right"
          autoClose={4000}
          newestOnTop
          // At most three at once: copying several contacts in a row stacked
          // toasts down the screen and over the controls being clicked.
          limit={3}
          theme="light"
          aria-label="Notifications"
        />
      </AuthProvider>
    </Router>
  );
}

export default App;

